/*
 * California Proposition 65 warning - visitor region detection.
 *
 * snippets/prop65-warning.liquid renders the warning block for products carrying
 * the prop65-warning tag. This file decides whether the visitor sees it. The
 * decision cannot be made in Liquid: product page HTML is served from Shopify's
 * CDN cache, so a server-rendered decision would be cached and then replayed to
 * whoever hits that copy next, in any state.
 *
 * FAIL DIRECTION - the most important thing in this file, and the opposite of
 * assets/geo-locate.js on the AU storefront. That one hides its message whenever
 * anything goes wrong, because withholding a delivery promise is the safe
 * direction. Here the safe direction is the other way: under-showing a legal
 * warning is the compliance risk, while showing it to a shopper in Nevada costs
 * nothing. So every failure - blocked request, timeout, non-JSON body, missing
 * fields - resolves to SHOW, and SHOW_WHEN_REGION_UNKNOWN below is the single
 * switch for that behaviour.
 *
 * The block is hidden while this file is still deciding, and revealed again by a
 * deadline so that a visitor never loses the warning to a script that stalls. The
 * head only arms its own fallback deadline for the case where this file never
 * runs at all, on DOMContentLoaded, which is guaranteed to fire after this
 * deferred script has already run. So the first thing this file does is claim
 * ownership of the deadline, which the head checks before it would otherwise
 * reveal the block itself - the two can never race and flash the warning in
 * before the real answer lands. See the prop65 comment in layout/theme.liquid.
 */

const SHOW_WHEN_REGION_UNKNOWN = true;

const REGION_ENDPOINT = "https://mp-geo-detect.harvey-1c7.workers.dev/region";
const REQUEST_TIMEOUT_MS = 3000;

// Must outlast the fetch above, so a normal slow answer still wins the deadline.
const REVEAL_DEADLINE_MS = REQUEST_TIMEOUT_MS + 2000;

function revealIfStillPending() {
  if (document.documentElement.getAttribute("data-prop65") === "pending") {
    document.documentElement.removeAttribute("data-prop65");
  }
}

/*
 * The endpoint owns the California determination and reports it as a single
 * boolean, so no consumer can invert the rule by treating "could not resolve"
 * as "not California". Anything that is not an explicit boolean - an older
 * response shape, an error body, a proxy's interstitial - means the region was
 * not determined, which shows the warning.
 */
function shouldShowWarning(payload) {
  if (payload && typeof payload.california === "boolean") {
    return payload.california;
  }

  return SHOW_WHEN_REGION_UNKNOWN;
}

async function resolveVisitorRegion() {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    // No credentials: this is an anonymous region lookup and must not send cookies.
    const response = await fetch(REGION_ENDPOINT, {
      signal: controller.signal,
      credentials: "omit",
    });

    if (!response.ok) {
      return SHOW_WHEN_REGION_UNKNOWN;
    }

    return shouldShowWarning(await response.json());
  } catch (error) {
    return SHOW_WHEN_REGION_UNKNOWN;
  } finally {
    clearTimeout(timeout);
  }
}

window.prop65DeadlineOwned = true;
const revealDeadline = setTimeout(revealIfStillPending, REVEAL_DEADLINE_MS);

resolveVisitorRegion().then((show) => {
  clearTimeout(revealDeadline);

  // On the root element rather than the block itself, so the answer survives the
  // product page re-rendering sections through the Section Rendering API (a
  // variant change re-renders product info) with no listener to re-attach.
  document.documentElement.setAttribute("data-prop65", show ? "show" : "hide");
});
